import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, RefreshCw, Copy, Check } from 'lucide-react';
import { api } from '../lib/api';
import { Dialog } from '../components/Dialog';
import { useToast } from '../components/Toast';

const SCOPES = ['Reports', 'PhoneSystemAdmin', 'Users', 'Trunks', 'GroupOwner', 'CallControl'];

export default function ServicePrincipals() {
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<any>({ scopes: ['Reports'] });
  const [shown, setShown] = useState<{ client_id: string; client_secret: string } | null>(null);
  const { data: list = [] } = useQuery({ queryKey: ['sp'], queryFn: () => api.servicePrincipals.list() });

  const create = useMutation({
    mutationFn: () => api.servicePrincipals.create(form),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ['sp'] }); setOpen(false); setShown({ client_id: r.client_id, client_secret: r.client_secret }); },
    onError: (e: any) => toast('error', e.message),
  });
  const regenerate = useMutation({
    mutationFn: (id: string) => api.servicePrincipals.regenerate(id),
    onSuccess: (r) => setShown({ client_id: r.client_id, client_secret: r.client_secret }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.servicePrincipals.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sp'] }),
  });

  function copy(text: string) {
    navigator.clipboard.writeText(text); toast('success', 'Copied');
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Service Principals</h1>
        <button onClick={() => { setForm({ scopes: ['Reports'] }); setOpen(true); }} className="btn-primary inline-flex items-center gap-2"><Plus size={14} /> Create</button>
      </div>
      <p className="text-sm text-textSecondary">Service principals issue 60-second OAuth2 tokens to 3rd-party apps via <code className="font-mono text-xs bg-elevated px-1">POST /connect/token</code>.</p>
      <div className="card p-0 overflow-hidden">
        <table className="table">
          <thead><tr><th>Number</th><th>Name</th><th>Client ID</th><th>Scopes</th><th>Last used</th><th></th></tr></thead>
          <tbody>
            {(list as any[]).map((sp: any) => (
              <tr key={sp.principal_uuid} className="hover:bg-elevated">
                <td className="font-mono">{sp.number}</td>
                <td>{sp.name}</td>
                <td className="font-mono text-xs flex items-center gap-1">{sp.client_id}<button onClick={() => copy(sp.client_id)} className="btn-ghost p-1"><Copy size={12} /></button></td>
                <td className="text-xs">{(sp.scopes || []).join(', ')}</td>
                <td className="text-xs">{sp.last_used_at ? new Date(sp.last_used_at).toLocaleString() : '—'}</td>
                <td className="space-x-1">
                  <button onClick={() => regenerate.mutate(sp.principal_uuid)} className="btn-ghost p-1" title="Regenerate secret"><RefreshCw size={14} /></button>
                  <button onClick={() => remove.mutate(sp.principal_uuid)} className="btn-ghost p-1 text-danger"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onClose={() => setOpen(false)} title="Create Service Principal" size="md"
        footer={<><button onClick={() => setOpen(false)} className="btn-ghost">Cancel</button><button onClick={() => create.mutate()} className="btn-primary">Create</button></>}>
        <div className="space-y-3">
          <div>
            <label className="block text-sm text-textSecondary mb-1">Number *</label>
            <input className="input font-mono" value={form.number || ''} onChange={(e) => setForm({ ...form, number: e.target.value })} placeholder="99000" />
          </div>
          <div>
            <label className="block text-sm text-textSecondary mb-1">Name *</label>
            <input className="input" value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="My CRM Integration" />
          </div>
          <div>
            <label className="block text-sm text-textSecondary mb-1">Scopes</label>
            <div className="space-y-1">
              {SCOPES.map((s) => (
                <label key={s} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={(form.scopes || []).includes(s)} onChange={(e) => {
                    const cur = form.scopes || [];
                    setForm({ ...form, scopes: e.target.checked ? [...cur, s] : cur.filter((x: string) => x !== s) });
                  }} />
                  {s}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm text-textSecondary mb-1">Group scope (optional)</label>
            <input className="input" value={form.group_scope || ''} onChange={(e) => setForm({ ...form, group_scope: e.target.value })} placeholder="restrict to single group / department" />
          </div>
        </div>
      </Dialog>

      <Dialog open={!!shown} onClose={() => setShown(null)} title="Save these credentials now" size="md"
        footer={<button onClick={() => setShown(null)} className="btn-primary">I've saved them</button>}>
        <div className="space-y-3">
          <p className="text-warning text-sm">⚠ The client_secret is shown once. Copy it now — it cannot be retrieved later.</p>
          <div>
            <label className="block text-sm text-textSecondary mb-1">client_id</label>
            <div className="flex gap-2"><input className="input font-mono" readOnly value={shown?.client_id || ''} /><button onClick={() => copy(shown?.client_id || '')} className="btn-ghost"><Copy size={14} /></button></div>
          </div>
          <div>
            <label className="block text-sm text-textSecondary mb-1">client_secret</label>
            <div className="flex gap-2"><input className="input font-mono" readOnly value={shown?.client_secret || ''} /><button onClick={() => copy(shown?.client_secret || '')} className="btn-ghost"><Check size={14} /></button></div>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
